/**
 * Fletcher's steps as a vertical list, and the parts both the ticket's
 * Fletcher card and the case's evidence pack read them with (YAG-1078):
 *
 *   - `QaSteps`: one row per step, the step's small screenshot (which opens
 *     large), "Step 2 · did not hold", the check in words, what Fletcher saw,
 *     and "Watch from 0:04";
 *   - `QaShot`: a step's small screenshot, opening large in a dialog;
 *   - `useQaVideoSeek`: a recording a step can jump into, before or after it
 *     has loaded.
 *
 * Everything fits the narrowest sheet: the list never scrolls sideways, and
 * text wraps inside its column.
 */
import { useCallback, useRef, useState } from "react";
import { QA_CHANGE_STEP_WORDS, qaVideoTime, type QaEvidence, type QaStepView } from "@yagni/shared";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogTitle,
  DialogTrigger,
} from "../ui/dialog.js";

export function qaCaptureExpired(
  capture: Pick<QaEvidence, "expiresAt">,
  now = Date.now(),
): boolean {
  return Date.parse(capture.expiresAt) <= now;
}

/** Seek now when the video knows its length, else as soon as it does. */
export function useQaVideoSeek() {
  const player = useRef<HTMLVideoElement>(null);
  const pending = useRef<number | null>(null);
  const seek = useCallback((seconds: number | null | undefined) => {
    if (seconds == null || !Number.isFinite(seconds) || seconds < 0) return;
    pending.current = seconds;
    const video = player.current;
    if (video && Number.isFinite(video.duration)) {
      video.currentTime = Math.min(seconds, video.duration);
      pending.current = null;
    }
  }, []);
  const onLoadedMetadata = useCallback(() => {
    if (pending.current != null) seek(pending.current);
  }, [seek]);
  return { player, seek, onLoadedMetadata };
}

/** A step's small screenshot; it opens large. A blank tile when there is none to show. */
export function QaShot({ capture, step }: { capture: QaEvidence | null; step: number }) {
  const [failed, setFailed] = useState(false);
  if (!capture || failed || qaCaptureExpired(capture)) {
    return (
      <span
        className="qa-shot qa-shot-none"
        aria-hidden="true"
        title={capture && !failed ? "This screenshot has expired." : undefined}
      />
    );
  }
  return (
    <Dialog>
      <DialogTrigger asChild>
        <button type="button" className="qa-shot" aria-label={`Open step ${step}'s screenshot`}>
          <img src={capture.url} alt="" loading="lazy" onError={() => setFailed(true)} />
        </button>
      </DialogTrigger>
      <DialogContent className="qa-shot-dialog max-w-[min(1120px,calc(100%-2rem))]">
        <DialogTitle className="qa-shot-dialog-title">{capture.title}</DialogTitle>
        <DialogDescription className="sr-only">
          The screenshot Fletcher kept at the end of step {step}.
        </DialogDescription>
        <img className="qa-shot-large" src={capture.url} alt={capture.title} />
      </DialogContent>
    </Dialog>
  );
}

export function QaSteps({
  steps,
  onWatch,
  label = "Steps Fletcher checked",
}: {
  steps: readonly QaStepView[];
  /** Jump the recording to where a step starts; absent when there is no recording to play. */
  onWatch?: (seconds: number, step: number) => void;
  label?: string;
}) {
  if (steps.length === 0) return null;
  return (
    <ol className="qa-steps" aria-label={label}>
      {steps.map((step) => (
        <li key={step.index} className="qa-step" data-status={step.outcome}>
          <QaShot capture={step.screenshot} step={step.index} />
          <div className="qa-step-main">
            <span className="qa-step-state">
              Step {step.index} · {QA_CHANGE_STEP_WORDS[step.outcome]}
            </span>
            <p className="qa-step-title">{step.caption}</p>
            {step.note && <p className="qa-step-note">{step.note}</p>}
          </div>
          {onWatch && step.atSeconds != null && (
            <button
              type="button"
              className="qa-step-watch"
              aria-label={`Watch from ${qaVideoTime(step.atSeconds)}, step ${step.index}`}
              onClick={() => onWatch(step.atSeconds!, step.index)}
            >
              Watch from {qaVideoTime(step.atSeconds)}
            </button>
          )}
        </li>
      ))}
    </ol>
  );
}
